import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import firebase from "firebase/compat/app";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { shallowEqual } from "react-redux";
import { Badge, Button, Card, CardBody, CardHeader, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";

import useProductsStorage from "resbutler-ui/hooks/useProductsStorage";
import { ProductCategoryId, ProductStock, ProductTypeId, SizeStock, StockByType, StockScope, StockStatus } from "resbutler-utils/types";

import { getConfig, getMealsFiltered } from "../../core/utilities";
import { useAppSelector } from "../../hooks/hook";

import { Labels } from "../../resbutler-utils/types/Labels";
import { Meals } from "../../resbutler-utils/types/Meals";
import { Menus } from "../../resbutler-utils/types/Menu";

import { useQueryClient } from "@tanstack/react-query";
import { refreshStockData, useStocks } from "hooks/useStocks";
import { difference, filter, find, findKey, forEach, keys, values as lodashValues, map, mapValues, pickBy, set, unset } from "lodash";
import { toast } from "react-toastify";
import { ProductSearchModal } from "resbutler-ui/components/ProductSearch/ProductSearchModal";
import { catchExceptionCallback } from "resbutler-ui/error";
import { FilterProps } from "./Filter";
import SpecialStockProductForm from "./SpecialStockProductForm";
import StockProductForm from "./StockProductForm";

enum CollapseTabType {
  Product,
  Modifier,
  Upsell,
  Addition,
  Special,
}

function validate(values: StockByType) {
  const errors: any = {};
  forEach(values.specials, (specialProduct, productId) => {
    const enabledMenus = keys(pickBy(specialProduct.enabledMenus, (m) => m.enabled));
    if (specialProduct.stockScope === StockScope.Menus) {
      forEach(enabledMenus, (menuId) => {
        if (!specialProduct.enabledMenus?.[menuId]?.groupHeadingId) set(errors, `specials.${productId}.enabledMenus.${menuId}.groupHeadingId`, "Required");
      });
    }
  });

  return errors;
}

interface MenuItemModalProps {
  setShowModal: (v: boolean) => void;
  restaurantId: string;
  date: string;
  currentDate: string;
}

const MenuItemModal = React.memo<MenuItemModalProps>(({ setShowModal, restaurantId, date, currentDate }) => {
  const {
    menus: allMenus,
    restaurants,
    productSizes,
  } = useAppSelector(
    (state) => ({
      menus: state.root.menus,
      restaurants: state.root.restaurants,
      productSizes: state.root.productSizes,
    }),
    shallowEqual
  );

  const { tenantId, client } = getConfig();
  const [, menuProducts] = useProductsStorage(true);
  const queryClient = useQueryClient();
  const { data: stockByType } = useStocks(Number(date), restaurantId);
  const [menus, setMenus] = useState<Menus>({});
  const [initializing, setInitializing] = useState(true);
  const [collapseTab, setCollapseTab] = useState<CollapseTabType | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [filters, setFilters] = useState<FilterProps>({
    query: "",
    overrides: false,
    menuId: "",
    typeIds: [],
  });

  const closeModal = useCallback(() => setShowModal(false), [setShowModal]);

  const onSubmit = async (values: StockByType) => {
    try {
      const promises = [];
      forEach(values.products, (item: ProductStock, id) => {
        /**Dont update sold field from frontend */
        forEach(item.sizes, (size) => {
          unset(size, "sold");
        });

        promises.push(
          firebase
            .firestore()
            .doc(`${client}/stocks/date/${date}/restaurants/${restaurantId}/products/${id}`)
            .set({ ...item, updatedAt: Date.now() }, { merge: true })
        );
      });

      forEach(values.specials, (item, id) => {
        /**Dont update sold field from frontend */
        forEach(item.sizes, (size) => {
          unset(size, "sold");
        });
        promises.push(
          item.removed
            ? firebase.firestore().doc(`${client}/stocks/date/${date}/restaurants/${restaurantId}/specials/${id}`).delete()
            : firebase
                .firestore()
                .doc(`${client}/stocks/date/${date}/restaurants/${restaurantId}/specials/${id}`)
                .set({ ...item, updatedAt: Date.now() }, { merge: true })
        );
      });
      await Promise.all(promises);
      refreshStockData(queryClient, client, Number(date), restaurantId);
      closeModal();
      toast.success("Stock Saved", {
        position: "bottom-right",
      });
    } catch (error) {
      catchExceptionCallback(error);
    }
  };

  useEffect(() => {
    if (!restaurantId) return;

    const init = async () => {
      try {
        setInitializing(true);

        const [labelsSnap, mealsSnap] = await Promise.all([firebase.database().ref(`${tenantId}/maestroDataLabels`).orderByChild("restaurantId").equalTo(restaurantId).once("value"), firebase.database().ref(`${tenantId}/meals`).orderByChild("enabled").equalTo(true).once("value")]);

        const labels = (labelsSnap.val() || {}) as Labels;
        const meals = (mealsSnap.val() || {}) as Meals;

        const restaurant = restaurants[restaurantId];

        const restaurantMeals = getMealsFiltered(date, restaurantId, labels, meals, restaurant.zoneId);

        const mealIds = Object.keys(restaurantMeals || {});

        const labelSnapshots = await Promise.all(mealIds.map((mealId) => firebase.database().ref(`${tenantId}/maestros/${date}/${restaurantId}/maestro/${mealId}/label`).once("value")));

        const restaurantLabels = labelSnapshots.map((s) => s.val());

        const maestroDataPromises = restaurantLabels.filter((l) => l?.labelId && l?.version).map((l) => firebase.database().ref(`${tenantId}/maestroData/${l.labelId}/${l.version}/style/menuIds`).once("value"));

        const menuSnapshots = await Promise.all(maestroDataPromises);

        const menuIds = Array.from(new Set(menuSnapshots.flatMap((snap) => Object.keys(snap.val() || {}))));

        const activeMenus: Menus = {};

        menuIds.forEach((id) => {
          if (allMenus?.[id]?.enabled) {
            activeMenus[id] = allMenus[id];
          }
        });

        setMenus(activeMenus);
      } catch (error) {
        catchExceptionCallback(error);
      } finally {
        setInitializing(false);
      }
    };

    init();
  }, [restaurantId, date, tenantId, restaurants, allMenus]);

  const filteredProducts = useMemo(() => {
    let result = menuProducts.filter((p) => p.productCategoryId === ProductCategoryId.SaleProducts && p.restaurants?.[restaurantId]?.enabled);

    if (filters.query.trim()) {
      const query = filters.query.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(query));
    }

    return result;
  }, [menuProducts, restaurantId, filters.query]);

  const ExpandableCard = ({ collapseValue, title, count, children, rightComponent }: { collapseValue: CollapseTabType; title: string; count: number; children: React.ReactNode; rightComponent?: React.ReactNode }) => {
    const open = collapseTab === collapseValue;

    return (
      <Card className="card-default mt-2">
        <CardHeader className="bg-secondary">
          <Row>
            <Col className="d-flex align-items-center justify-content-between">
              <div>
                <Button type="button" className="me-2" onClick={() => setCollapseTab(open ? null : collapseValue)}>
                  <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                </Button>

                <strong>{title}</strong>

                {count > 0 && (
                  <Badge color="dark" pill className="ms-1">
                    {count}
                  </Badge>
                )}

                {initializing && <Spinner size="sm" className="ms-2" />}
              </div>
              <div>{rightComponent ? rightComponent : null}</div>
            </Col>
          </Row>
        </CardHeader>

        {open && <CardBody>{children}</CardBody>}
      </Card>
    );
  };

  return (
    <Modal top scrollable isOpen toggle={closeModal} size="xl">
      <Form onSubmit={onSubmit} initialValues={stockByType} validate={validate}>
        {({ handleSubmit, submitting, values, form }) => {
          return (
            <form onSubmit={handleSubmit} className="modal-content">
              <ModalHeader toggle={closeModal}>Menu Items</ModalHeader>

              <ModalBody>
                <div>
                  <div className="d-flex justify-content-between">
                    <div className="d-flex gap-4">
                      <div>
                        <input type="search" className="form-control" placeholder="Search..." value={filters.query} onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))} />
                      </div>

                      <div className="checkbox c-checkbox ">
                        <label className="mb-2 pe-2">
                          <input
                            type="checkbox"
                            checked={filters.overrides}
                            onChange={(e) =>
                              setFilters((f) => ({
                                ...f,
                                overrides: e.target.checked,
                              }))
                            }
                          />
                          Overrides Only
                        </label>
                      </div>
                    </div>
                  </div>

                  <ExpandableCard collapseValue={CollapseTabType.Product} title="Product" count={filter(values.products, (p) => !!findKey(p.sizes, (s) => s.enabled)).length}>
                    <StockProductForm date={Number(date)} menus={menus} restaurantId={restaurantId} productSizes={productSizes} filteredProducts={filters.overrides ? filteredProducts.filter((p) => find(values.products?.[p.id]?.sizes, (s) => s.enabled)) : filteredProducts} />
                  </ExpandableCard>

                  <ExpandableCard
                    collapseValue={CollapseTabType.Special}
                    title="Special"
                    count={filter(values.specials, (p) => !!findKey(p.sizes, (s) => s.enabled)).length}
                    rightComponent={
                      <Button type="button" color="primary" onClick={() => setShowProductModal(true)}>
                        Add Special
                      </Button>
                    }
                  >
                    <SpecialStockProductForm date={Number(date)} menus={menus} restaurantId={restaurantId} productSizes={productSizes} filteredProducts={filteredProducts.filter((p) => !values.specials?.[p.id]?.removed && find(values.specials?.[p.id]?.sizes, (s) => !!s))} />
                  </ExpandableCard>

                  <ProductSearchModal
                    restaurants={{ [restaurantId]: restaurants[restaurantId] }}
                    initialValues={map(
                      pickBy(values.specials, (v) => !v.removed),
                      (v, id) => {
                        const data = {
                          productId: id,
                          sizes: mapValues(v.sizes, () => true),
                        };
                        return data;
                      }
                    )}
                    open={showProductModal}
                    setOpen={setShowProductModal}
                    onCloseData={(data) => {
                      let specials = {};
                      if (data.length) {
                        const productsById = data.reduce((accu, current) => {
                          accu[current.productId] = pickBy(current.sizes, (v) => !!v);
                          return accu;
                        }, {});
                        specials = mapValues(productsById, (sizes, productId) => {
                          const sizeIds = Object.keys(sizes || {});
                          const sizeStocks: { [id: string]: SizeStock } = {};
                          if (sizeIds.length === 0) return null;
                          sizeIds.map((sizeId) => {
                            sizeStocks[sizeId] = { enabled: false, isStockLimitEnabled: false, status: StockStatus.SoldOut, ...values.specials?.[productId]?.sizes?.[sizeId] };
                          });

                          const productStock: ProductStock = {
                            productId: productId,
                            createdAt: Date.now(),
                            updatedAt: Date.now(),
                            date: Number(date),
                            restaurantId,
                            stockScope: StockScope.Menus,
                            ...values.specials?.[productId],
                            sizes: sizeStocks,
                          };
                          delete productStock.removed;
                          return productStock;
                        });
                      }

                      const newSpecials = pickBy(specials, (v) => !!v);
                      const removedIds = difference(keys(values.specials), keys(newSpecials));
                      const specialsToBeRemoved = {};
                      removedIds.forEach((id) => {
                        specialsToBeRemoved[id] = { ...values.specials[id], removed: true };
                      });
                      form.change(`specials`, { ...specialsToBeRemoved, ...newSpecials });

                      setShowProductModal(false);
                    }}
                    productTypeIds={[ProductTypeId.food, ProductTypeId.combinationFoodAndBeverage, ProductTypeId.beverage]}
                    restaurantId={restaurantId}
                    productSizes={lodashValues(productSizes)}
                  />
                </div>
              </ModalBody>

              <ModalFooter>
                <Button onClick={closeModal} disabled={submitting}>
                  Close
                </Button>

                {Number(date) >= Number(currentDate) && (
                  <Button id="save-block" type="submit" color="success" disabled={submitting}>
                    Save
                    {submitting && (
                      <span className="ps-2">
                        <Spinner size="sm" />
                      </span>
                    )}
                  </Button>
                )}
              </ModalFooter>
            </form>
          );
        }}
      </Form>
    </Modal>
  );
});

export default MenuItemModal;
